import type { Persona } from '@/domain/types';

export type PermissionScope = 'network' | 'assigned-studios';

export type PermissionCapability =
  | 'bookings'
  | 'comms'
  | 'customerRecords'
  | 'deposits'
  | 'rapportNotes'
  | 'availability'
  | 'settings'
  | 'financials';

export type PermissionAccess = 'none' | 'view' | 'edit';

export interface RolePermission {
  role: string; // display label, matches the legacy ROLE_MATRIX rows
  personaKey: Persona; // maps to the active persona for money-visibility enforcement
  scope: PermissionScope;
  capabilities: Record<PermissionCapability, PermissionAccess>;
}

export const PERMISSION_CAPABILITIES: PermissionCapability[] = [
  'bookings',
  'comms',
  'customerRecords',
  'deposits',
  'rapportNotes',
  'availability',
  'settings',
  'financials',
];

export const PERMISSION_ACCESS_LEVELS: PermissionAccess[] = ['none', 'view', 'edit'];

export const CAPABILITY_LABELS: Record<PermissionCapability, string> = {
  bookings: 'Bookings',
  comms: 'Comms',
  customerRecords: 'Customer records',
  deposits: 'Deposits',
  rapportNotes: 'Rapport notes',
  availability: 'Availability',
  settings: 'Settings',
  financials: 'Financials',
};

export const ACCESS_LABELS: Record<PermissionAccess, string> = {
  none: 'None',
  view: 'View',
  edit: 'Edit',
};

export const SCOPE_LABELS: Record<PermissionScope, string> = {
  network: 'Network',
  'assigned-studios': 'Assigned studios',
};

function caps(
  level: PermissionAccess,
  ...on: PermissionCapability[]
): Record<PermissionCapability, PermissionAccess> {
  const base = Object.fromEntries(
    PERMISSION_CAPABILITIES.map((c) => [c, 'none']),
  ) as Record<PermissionCapability, PermissionAccess>;
  for (const c of on) base[c] = level;
  return base;
}

/**
 * Recording a rebook outcome — the outcome choice, decline reason, and any
 * payment adjustment — is the studio manager's job at the make-sale/check-out.
 * Artists and apprentices only assist conversationally (they leave a lightweight
 * in-chair intent signal that pre-fills the manager's panel), so they cannot
 * finalise the outcome. Owner + Georgina retain full network access.
 *
 * Explicit allowlist: any persona not named here (including the bookings agent,
 * who works the phones rather than the floor make-sale) is denied.
 */
export function canRecordRebookOutcome(persona: Persona): boolean {
  return persona === 'studio-manager' || persona === 'georgina' || persona === 'owner';
}

/**
 * Who can authorise a no-deposit booking. Only Georgina and the owner may either
 * waive a deposit directly at booking time (still note-forced + logged) or
 * approve/reject the override requests that non-privileged personas raise. Every
 * other persona (studio manager, bookings agent, artist, apprentice) must submit
 * a request that lands in Georgina's Approvals queue instead of booking directly.
 *
 * Explicit allowlist — any persona not named here is denied, so the request path
 * is exactly `!canApproveNoDepositOverride(persona)`.
 */
export function canApproveNoDepositOverride(persona: Persona): boolean {
  return persona === 'georgina' || persona === 'owner';
}

/**
 * Who may sign off (approve) a reconciliation exception resolution — the
 * maker-checker on unmatched money (diagram 11). ONLY the Finance Manager, a
 * narrow signer whose sole job is this sign-off. Deliberately NOT the owner:
 * diagram 11 grants the audit-trail sign-off to the Finance Manager and names no
 * owner bypass (USER DECISION: no owner fallback). This is distinct from the Ops
 * no-deposit override (canApproveNoDepositOverride) — the recon sign-off reused
 * that Ops clearance before tv-vhs, conflating two different approvals; diagram
 * 01 is explicit that unmatched-money sign-off is "not routed through Ops".
 * Self-approval is blocked separately in reconResolutionFlow.
 *
 * Explicit allowlist — any persona not named here (including the Finance /
 * Accounts maker who resolves the row) is denied.
 */
export function canApproveReconResolution(persona: Persona): boolean {
  return persona === 'finance-manager';
}

/**
 * Who can reinstate a forfeited (lapsed) deposit (G2). Reversing a forfeit is a
 * financial goodwill decision, so it is management-only — the same allowlist as
 * the no-deposit override authority. Non-privileged personas see no button.
 */
export function canReinstateLapsedDeposit(persona: Persona): boolean {
  return persona === 'georgina' || persona === 'owner';
}

/**
 * Who may press the confirmations "Confirmed" button. Confirming is an Ops/desk
 * action; artists and apprentices never confirm a booking themselves - when a
 * client will only respond to the artist, Ops asks the artist to reach out and
 * then Ops marks it confirmed (vault-06 artist-tries path). Explicit denylist of
 * the two hands-off personas; everyone else (georgina, owner, studio-manager,
 * bookings-agent) may confirm.
 */
export function canMarkConfirmed(persona: Persona): boolean {
  return persona !== 'artist' && persona !== 'apprentice';
}

/**
 * Who may reach the back-office finance screens: Reconcile (/reconciliation, the
 * finance seats' landing) and the Day sheet (/calculations). tv-1xc — the two
 * finance seats (Finance / Accounts, Finance Manager) plus the management
 * personas that already reached these screens before the seats existed
 * (Georgina, Studio Manager, Owner). Centralises the App route guard so the two
 * routes share one predicate.
 *
 * Deliberately an explicit allowlist, NOT a raw `financials !== 'none'` matrix
 * read: the bookings agent (edit) and the artist (view) also have non-'none'
 * financials, so a matrix-derived gate would broaden the finance screens to
 * them. The intended set is management + finance only, so it is named here.
 */
export function canAccessFinanceScreens(persona: Persona): boolean {
  return (
    persona === 'georgina' ||
    persona === 'studio-manager' ||
    persona === 'owner' ||
    persona === 'finance' ||
    persona === 'finance-manager'
  );
}

export const DEFAULT_ROLE_PERMISSIONS: RolePermission[] = [
  {
    role: 'Georgina',
    personaKey: 'georgina',
    scope: 'network',
    capabilities: caps('edit', ...PERMISSION_CAPABILITIES),
  },
  {
    role: 'Studio Manager',
    personaKey: 'studio-manager',
    scope: 'assigned-studios',
    capabilities: { ...caps('edit', 'bookings', 'comms'), financials: 'view' },
  },
  {
    role: 'Bookings Agent',
    personaKey: 'bookings-agent',
    scope: 'assigned-studios',
    capabilities: caps('edit', 'bookings', 'customerRecords', 'deposits', 'financials'),
  },
  {
    role: 'Artist',
    personaKey: 'artist',
    scope: 'assigned-studios',
    // Artists read money, they do not move it: 'view' unmasks amounts (their payout
    // envelope, their bookings' prices and deposits) without granting deposit or
    // payment edit rights. Apprentices stay 'none'.
    capabilities: { ...caps('edit', 'rapportNotes', 'availability'), financials: 'view' },
  },
  {
    role: 'Apprentice',
    personaKey: 'apprentice',
    scope: 'assigned-studios',
    capabilities: caps('edit', 'customerRecords', 'rapportNotes'),
  },
  {
    role: 'Owner',
    personaKey: 'owner',
    scope: 'network',
    capabilities: caps('edit', 'settings', 'financials'),
  },
  {
    // The accountant who runs the money loop (reconciliation, settlement, day
    // close) — diagram 11. Edits financials; reads the deposit ledger and
    // customer records to reconcile, but does not take deposits or move bookings.
    // Reconciliation is daily and cross-studio, so scope is network.
    role: 'Finance / Accounts',
    personaKey: 'finance',
    scope: 'network',
    capabilities: { ...caps('view', 'customerRecords', 'deposits'), financials: 'edit' },
  },
  {
    // A narrow signer whose only job is to approve what Accounts resolved
    // (maker-checker on unmatched money) — diagram 11. Reads financials to sign
    // off; the approval right itself is enforced by the recon sign-off permission
    // (tv-vhs), not by a matrix capability. No settings.
    role: 'Finance Manager',
    personaKey: 'finance-manager',
    scope: 'network',
    capabilities: caps('view', 'financials'),
  },
];
